import { Field, Form } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import isEmpty from 'lodash/isEmpty'

import Button from 'common/components/button'
import FormItem from 'common/components/FormItem'
import Input from 'common/components/Input'
import Modal from 'common/components/Modal'
import SingleSelect from 'common/components/singleSelect'
import Table, { Column } from 'common/components/table'
import Tooltip from 'common/components/Tooltip'
import Emoji from 'common/emoji'
import { Add as AddIcon, Minus as MinusIcon } from 'common/icons'

type Props = {
  isOpen: boolean
  fileName: string
  data: string[][]
  headers: string[]
  onCancel: () => void
  onImport: (values: FormValues) => void
}

type FormValues = {
  fileName: string
  dataSeparator: 'columns' | 'delimiter'
  categoryLevel: string[]
  productIdColumn: string
  productNameColumn: string
  delimiter: ';' | '>'
  categoryColumn: string
}

const Condition = ({ when, is, children }) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (value === is ? children : null)}
  </Field>
)

const validateNotEmptyString = (value: string | undefined) =>
  value?.trim() === undefined || value.trim().length === 0
    ? 'Required'
    : undefined

const validateNotUndefined = (value: string | undefined) =>
  value === undefined ? 'Required' : undefined

const CsvProductPreview = ({
  isOpen,
  fileName,
  data,
  headers,
  onCancel,
  onImport,
}: Props) => {
  const columns: Column[] = headers.map((value) => ({
    Header: value,
    accessor: value,
    width: 200,
  }))
  const handleSave = (values: FormValues) => {
    onImport(values)
  }

  return (
    <Form<FormValues>
      onSubmit={handleSave}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={{
        fileName: fileName.substring(0, fileName.lastIndexOf('.')) || fileName,
        categoryLevel: ['', ''],
      }}
      render={({
        handleSubmit,
        hasValidationErrors,
        form: {
          mutators: { push, pop },
        }, // injected from final-form-arrays above
      }) => {
        return (
          <Modal
            visible={isOpen}
            centered
            onClose={() => {
              onCancel()
            }}
            isResponsive
            className="block"
            style={{
              maxWidth: '80vw',
              minWidth: 700,
            }}
            footer={
              <div className="my-3">
                <Button
                  data-cy="cancel-button"
                  className="mr-4 pointer-events-auto"
                  ghost
                  onClick={() => {
                    onCancel()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={(event) => {
                    handleSubmit(event)
                  }}
                  disabled={hasValidationErrors}
                >
                  Import
                </Button>
              </div>
            }
          >
            <div className="text-2xl flex flex-grow-0 flex-row gap-4 mb-6">
              <Emoji symbol="🤩" />
              <div className="font-bold">{fileName}</div>
            </div>

            <form onSubmit={handleSubmit} className="text-sm ">
              <Field<string | undefined>
                name="fileName"
                validate={validateNotEmptyString}
                render={({ input, meta }) => (
                  <FormItem
                    className="max-w-100"
                    label="Data source name"
                    guidingText="This will be displayed in the data source table."
                    htmlFor="fileName"
                    error={meta.error}
                  >
                    <Input
                      type="text"
                      id="fileName"
                      variant="default"
                      className="text-coolGray-800"
                      value={input.value}
                      name={input.name}
                      onChange={input.onChange}
                      maxLength={50}
                      required
                    />
                  </FormItem>
                )}
              />
              <div className="w-full border-coolGray-200 border border-solid mb-6" />
              <div className="text-xl font-medium text-coolGray-800">
                Define you data structure
              </div>
              <div className="text-sm text-coolGray-400 mb-10">
                Description to define what his means?
              </div>
              <div className="flex gap-4">
                <Field<string>
                  name="productNameColumn"
                  validate={validateNotUndefined}
                  render={({ input, meta }) => (
                    <FormItem
                      label="Product name column"
                      htmlFor="customerNameColumn"
                      className="max-w-55"
                      error={meta.error}
                    >
                      <SingleSelect
                        id="productNameColumn"
                        onChange={(val) => input.onChange(val?.value)}
                        value={input.value}
                        isSearchable={true}
                        required
                        options={headers.map((value) => ({
                          label: value,
                          value,
                        }))}
                      />
                    </FormItem>
                  )}
                />
                <Field<string>
                  name="productIdColumn"
                  render={({ input, meta }) => (
                    <FormItem
                      label="Product ID (Optional)"
                      htmlFor="customerIdColumn"
                      className="max-w-55"
                      error={meta.error}
                    >
                      <SingleSelect
                        id="productIdColumn"
                        onChange={(val) => input.onChange(val?.value)}
                        value={input.value}
                        isSearchable={true}
                        options={headers.map((value) => ({
                          label: value,
                          value,
                        }))}
                      />
                    </FormItem>
                  )}
                />
                <Field<string>
                  name="dataSeparator"
                  validate={validateNotUndefined}
                  render={({ input, meta }) => (
                    <FormItem
                      label="How your data are separated"
                      htmlFor="dataSeparator"
                      className="max-w-55"
                      error={meta.error}
                    >
                      <SingleSelect
                        id="dataSeparator"
                        onChange={(val) => input.onChange(val?.value)}
                        value={input.value}
                        isSearchable={true}
                        options={[
                          { label: 'Column', value: 'columns' },
                          {
                            label: 'Delimiter (e.g. ; or >)',
                            value: 'delimiter',
                          },
                        ]}
                      />
                    </FormItem>
                  )}
                />
                <Condition when="dataSeparator" is="delimiter">
                  <Field<string>
                    name="delimiter"
                    validate={validateNotUndefined}
                    render={({ input, meta }) => (
                      <FormItem
                        label="Delimiter"
                        htmlFor="delimiter"
                        className="max-w-55"
                        error={meta.error}
                      >
                        <SingleSelect
                          id="delimiter"
                          onChange={(val) => input.onChange(val?.value)}
                          value={input.value}
                          isSearchable={true}
                          options={[
                            { label: ';', value: ';' },
                            { label: '>', value: '>' },
                          ]}
                        />
                      </FormItem>
                    )}
                  />
                </Condition>
              </div>

              <Condition when="dataSeparator" is="columns">
                <div className="flex gap-4 flex-wrap items-start">
                  <FieldArray name="categoryLevel">
                    {({ fields }) =>
                      fields.map((name, index) => (
                        <div
                          key={name}
                          className="inline-flex items-center gap-4"
                        >
                          <Field<string>
                            name={`${name}`}
                            validate={validateNotEmptyString}
                            render={({ input, meta }) => (
                              <FormItem
                                label={`Category level${index + 1}`}
                                htmlFor={`categoryLevel${index}`}
                                error={meta.error}
                                orientation="horizontal"
                              >
                                <div className="flex gap-4">
                                  <SingleSelect
                                    id={`categoryLevel${index}`}
                                    className="flex-1 w-55"
                                    onChange={(val) =>
                                      input.onChange(val?.value)
                                    }
                                    value={input.value}
                                    isSearchable={true}
                                    options={headers
                                      .filter((value) =>
                                        fields.value[index] === value
                                          ? true
                                          : !fields.value.includes(value)
                                      )
                                      .map((value) => ({
                                        label: value,
                                        value,
                                      }))}
                                  />

                                  {fields.length !== undefined &&
                                    index === fields.length - 1 && (
                                      <div className="h-10 flex gap-1 items-stretch">
                                        <Tooltip overlay="Add sequence column to the table">
                                          <Button
                                            ghost
                                            className="h-full"
                                            onClick={() =>
                                              push('categoryLevel', undefined)
                                            }
                                            disabled={
                                              fields.length ===
                                              Math.min(headers.length, 5)
                                            }
                                            prefixIcon={
                                              <AddIcon
                                                size={4}
                                                isDefaultColor={false}
                                              />
                                            }
                                          />
                                        </Tooltip>
                                        <Tooltip overlay="Remove a sequence column from the table">
                                          <Button
                                            className="self-end h-full"
                                            disabled={fields.length === 1}
                                            ghost
                                            onClick={() => pop('categoryLevel')}
                                            prefixIcon={
                                              <MinusIcon
                                                size={4}
                                                isDefaultColor={false}
                                              />
                                            }
                                          />
                                        </Tooltip>
                                      </div>
                                    )}
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>
                      ))
                    }
                  </FieldArray>
                </div>

                <div className="mt-3">
                  <FieldArray name="categoryLevel">
                    {({ fields }) => (
                      <div className="text-xs text-coolGray-600">
                        {fields.value
                          .filter((value) => !isEmpty(value))
                          .join(' / ')}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </Condition>
              <Condition when="dataSeparator" is="delimiter">
                <Field<string>
                  name="categoryColumn"
                  validate={validateNotUndefined}
                  render={({ input, meta }) => (
                    <FormItem
                      label="Category column"
                      htmlFor="categoryColumn"
                      className="max-w-55"
                      error={meta.error}
                    >
                      <SingleSelect
                        id="categoryColumn"
                        onChange={(val) => input.onChange(val?.value)}
                        value={input.value}
                        isSearchable={true}
                        required
                        options={headers.map((value) => ({
                          label: value,
                          value,
                        }))}
                      />
                    </FormItem>
                  )}
                />
              </Condition>
            </form>

            <Table
              columns={columns}
              data={data}
              className="mt-6 bg-coolGray-50 sm:-ml-6 sm:-mr-6 border-t border-coolGray-200"
            />
          </Modal>
        )
      }}
    />
  )
}

const CsvProductPreviewWithResetState = (props: Props) => (
  <CsvProductPreview key={`${props.isOpen}`} {...props} />
)

export default CsvProductPreviewWithResetState
